import {ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, ViewEncapsulation} from '@angular/core';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {Observable, timer} from 'rxjs';
import {filter, switchMap, tap} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {FuseConfigService} from '@fuse/services/config.service';
import {FuseSidebarService} from '@fuse/components/sidebar/sidebar.service';

import {navigation} from 'app/navigation/navigation';
import {AuthService} from '../../../kluh-manager/auth.service';
import {BackgroundImageService} from '../../../kluh-manager/helpers/background-image.service';
import {FirebaseUserDetails, ManagerUser} from '../../../kluh-manager/models';
import {ActivatedRoute, Router} from '@angular/router';
import {R2CloudClientService} from '../../../kluh-manager/ws/r2-cloud-client.service';
import {ImageFileService} from '../../../kluh-manager/main/image-file/image-file-service';
import {ComponentCleaner} from '../../../kluh-manager/component-cleaner';
import {ScriptExecutionService} from '../../../kluh-manager/main/script-execution/script-execution.service';
import {DesktopServerService} from '../../../kluh-manager/main/desktop-server/desktop-server.service';
import {UserAuthorityDaoService} from '../../../kluh-manager/main/user-authority/user-authority-dao.service';
import {clientVersion} from '../../../../client-version';
import {CustomLayoutService} from '../../../kluh-manager/main/custom-layout/custom-layout.service';
import {CustomerGroupTemplate} from '../../../kluh-manager/models-custom-layout';
import {CustomerGroupTemplateDaoService} from '../../../kluh-manager/main/custom-layout/customer-group-template/customer-group-template-dao.service';
import {CookieService} from 'ngx-cookie-service';
import {PermissionDAOService} from '../../../kluh-manager/main/iam/permission-dao.service';
import {CustomerGroupService} from '../../../kluh-manager/main/customer-group/customer-group.service';
import {R2MenuService} from '../../../kluh-manager/utils/r2-menu.service';
import {Title} from '@angular/platform-browser';
import {FooterService} from '../footer/footer.service';
import {CustomerGroupDaoService} from '../../../kluh-manager/main/customer-group/customer-group-dao.service';
import {customLayoutUrlCookieName} from '../../../kluh-manager/helpers/constants';
import {isWindowsOS} from '../../../kluh-manager/helpers/kluh';
import {ClientDownloadService} from '../../../kluh-manager/main/desktop/client-download.service';
import {ContingencyService} from '../../../kluh-manager/main/login/contingency.service';
import {FormControl} from '@angular/forms';
import {ConfirmDialogComponent} from '../../../kluh-manager/helpers/confirm-dialog/confirm-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {LocalClientService} from '../../../kluh-manager/main/login/local-client.service';
import {finishLogoutEitherWay} from '../../../kluh-manager/logout-completion';

@Component({
    selector: 'toolbar',
    templateUrl: './toolbar.component.html',
    styleUrls: ['./toolbar.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})


export class ToolbarComponent extends ComponentCleaner implements OnInit {
    horizontalNavbar!: boolean;
    rightNavbar!: boolean;
    hiddenNavbar!: boolean;
    languages: any;
    navigation: any;
    selectedLanguage: any;
    userStatusOptions!: any[];
    customerGroupHasTemplate = true;
    user$!: Observable<FirebaseUserDetails | null>;
    public managerUser!: ManagerUser;
    clientVersion = clientVersion;
    customerGroupTemplate!: CustomerGroupTemplate;
    clientStatusEnabled = isWindowsOS();
    loadingCustomGroupTemplate = true;
    contingencyModeControl: FormControl<boolean | null> = new FormControl<boolean | null>(false);
    isDarkMode = false;
    contingencyEnabled = toSignal(this.contingencyService.isEnabled$());
    private readonly destroyRef = inject(DestroyRef);

    /**
     * Constructor
     *
     * @param {FuseConfigService} _fuseConfigService
     * @param {FuseSidebarService} _fuseSidebarService
     * @param {TranslateService} _translateService
     * @param {AuthService} auth
     * @param footerService
     * @param r2MenuService
     * @param activatedRoute
     * @param userAuthorityDao
     * @param permissionDAOService
     * @param customLayoutService
     * @param customerGroupTemplateDaoService
     * @param customerGroupService
     * @param customerGroupDaoService
     * @param {BackgroundImageService} backgroundImageService
     * @param titleService
     * @param cookieService
     * @param desktopServerService
     * @param {ImageFileService} imageFileService
     * @param scriptExecutionService
     * @param {Router} router
     * @param {R2CloudClientService} clientService
     */
    constructor(
        private _fuseConfigService: FuseConfigService,
        private _fuseSidebarService: FuseSidebarService,
        private _translateService: TranslateService,
        private auth: AuthService,
        private footerService: FooterService,
        private r2MenuService: R2MenuService,
        private activatedRoute: ActivatedRoute,
        private userAuthorityDao: UserAuthorityDaoService,
        public clientDownload: ClientDownloadService,
        private permissionDAOService: PermissionDAOService,
        private customLayoutService: CustomLayoutService,
        private customerGroupTemplateDaoService: CustomerGroupTemplateDaoService,
        private customerGroupService: CustomerGroupService,
        private customerGroupDaoService: CustomerGroupDaoService,
        private backgroundImageService: BackgroundImageService,
        private titleService: Title,
        private cookieService: CookieService,
        public desktopServerService: DesktopServerService,
        public imageFileService: ImageFileService,
        private scriptExecutionService: ScriptExecutionService,
        private router: Router,
        public contingencyService: ContingencyService,
        private dialog: MatDialog,
        private clientService: R2CloudClientService,
        private localClientService: LocalClientService,
        private cdr: ChangeDetectorRef,
    ) {
        super();
        this.clientService.enableChanges().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
            this.clientStatusEnabled = result;
            if (!isWindowsOS()) {
                this.clientStatusEnabled = false;
            }
            this.cdr.markForCheck();
        });
        this.user$ = userAuthorityDao.getMe();
        // Set the defaults
        this.userStatusOptions = [
            {
                title: 'Online',
                icon: 'icon-checkbox-marked-circle',
                color: '#4CAF50'
            },
            {
                title: 'Away',
                icon: 'icon-clock',
                color: '#FFC107'
            },
            {
                title: 'Do not Disturb',
                icon: 'icon-minus-circle',
                color: '#F44336'
            },
            {
                title: 'Invisible',
                icon: 'icon-checkbox-blank-circle-outline',
                color: '#BDBDBD'
            },
            {
                title: 'Offline',
                icon: 'icon-checkbox-blank-circle-outline',
                color: '#616161'
            }
        ];

        this.languages = [
            {
                id: 'en',
                title: 'English',
                flag: 'us'
            },
            {
                id: 'pt-br',
                title: 'Brazilian Portuguese',
                flag: 'br'
            }
        ];

        this.navigation = navigation;

        this.setCustomLayout();

        this.contingencyModeControl.setValue(this.contingencyService.isEnabled());

        this.contingencyService.isEnabled$().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isEnabled) => {
            this.contingencyModeControl.setValue(isEnabled);
            this.cdr.markForCheck();
        });

        this.isDarkMode = localStorage.getItem('r2cloud-color-theme') === 'theme-blue-gray-dark';

    }

    setCustomLayout(): void {
        this.user$.pipe(
            filter((user): user is FirebaseUserDetails => !!user),
            switchMap(user => this.customerGroupDaoService.getByAuthenticatedManagerUser().pipe(
                filter(customerGroup => !!customerGroup),
                tap(customerGroup => {
                    this.customerGroupHasTemplate = !(customerGroup.customPortalTemplateId === null || customerGroup.customPortalTemplateId === 0);
                }),
                switchMap(() => timer(2200)),
                switchMap(() => this.customerGroupTemplateDaoService.findOneByManagerUserId(user.managerUser.id ?? 0))
            )),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe({
            next: (customerGroupTemplate) => {
                if (customerGroupTemplate) {
                    this.customerGroupTemplateDaoService.setCurrentCustomerGroupTemplate(customerGroupTemplate);
                    this.customerGroupTemplate = customerGroupTemplate;
                    this.customLayoutService.setDynamicStyle(this.customerGroupTemplate?.customPortalTemplate?.css ?? '');
                    this.cookieService.set(customLayoutUrlCookieName, this.customerGroupTemplate?.url ?? '');
                    if (this.customerGroupTemplate?.customPortalTemplate?.title) {
                        this.titleService.setTitle(this.customerGroupTemplate?.customPortalTemplate.title);
                    }
                    if (this.customerGroupTemplate?.customPortalTemplate?.hasCustomProtocol) {
                        this.clientService.initStartCustomProtocolObservable(this.customerGroupTemplate?.customPortalTemplate?.customProtocol ?? '');
                    }
                    if (this.customerGroupTemplate?.backgroundImageTemplate?.imageFileTemplateIds && !this.backgroundImageService.backgroundIsLoaded()) {
                        this.backgroundImageService.setRandomBackground(this.customerGroupTemplate?.backgroundImageTemplate?.imageFileTemplateIds);
                    }
                }
                this.loadingCustomGroupTemplate = false;
                this.cdr.markForCheck();
            },
            error: () => {
                this.loadingCustomGroupTemplate = false;
                this.cdr.markForCheck();
            }
        });
    }

    onMenuOpen(): void {
        // console.log('menu: Open');
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to the config changes
        this._fuseConfigService.config
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((settings: any) => {
                this.horizontalNavbar = settings.layout.navbar.position === 'top';
                this.rightNavbar = settings.layout.navbar.position === 'right';
                this.hiddenNavbar = settings.layout.navbar.hidden === true;
                this.cdr.markForCheck();
            });

        // Set the selected language from default languages
        this.selectedLanguage = this.languages.find((l: any) => l.id === this._translateService.currentLang);

        this.checkFirstCustomerGroupLoad();

    }

    checkFirstCustomerGroupLoad(): void {
        setTimeout(() => {
            const url = new URL(window.location.href);
            const params = new URLSearchParams(url.search);
            const customerGroupId = params.get('id');
            if (customerGroupId != null) {
                this.r2MenuService.hideMenuWithoutPermission(+customerGroupId);
            }
        }, 2500);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------


    /**
     * Toggle sidebar open
     *
     * @param key
     */
    toggleSidebarOpen(key: any): void {
        this._fuseSidebarService.getSidebar(key)?.toggleOpen();
    }

    toggleSidebarFolded(key: string): void {
        this._fuseSidebarService.getSidebar(key)?.toggleFold();
    }


    helpLink(): void {
        this.customerGroupTemplateDaoService.getHelpLink();
    }


    /**
     * Search
     *
     * @param value
     */
    search(value: any): void {
        // Do your search here...
        console.debug(value);
    }

    /**
     * Set the language
     *
     * @param lang
     */
    setLanguage(lang: any): void {
        // Set the selected language for the toolbar
        this.selectedLanguage = lang;

        // Use the selected language for translations
        this._translateService.use(lang.id);
    }

    logout(): void {
        // this.managerStompService.disconnect();
        this.footerService.closeTerminal();
        this.auth.logoutMicroservices$().subscribe((_results) => {
                this.apiLogout();
            },
            _error => {
                this.apiLogout();
            }
        );
    }

    // R2CLOUD-1166: o desfecho é o MESMO no sucesso e no erro. Este `subscribe` só tinha ramo `next`,
    // e a única navegação de saída do portal mora dentro dele — bastava o `GET /api/logout` errar
    // (o Firebase já foi deslogado antes dele, então o re-login do R2CloudHttpBase falha por
    // construção) para a SPA ficar parada em `/desktop`, sem toolbar e sem formulário de login.
    apiLogout(): void {
        this.auth.logout().subscribe(finishLogoutEitherWay(() => {
            this.backgroundImageService.clearCache();
            this.customLayoutService.removeAllCustomStyle();
            this.customerGroupService.destroyer();
            this.permissionDAOService.clearCache();
            setTimeout(() => {
                window.location.href = '/login?url=' + this.cookieService.get(customLayoutUrlCookieName);
            }, 200);
        }));
    }

    goToHome(): void {
        this.router.navigate(['/']);
    }

    toggleContingencyMode(): void {
        this.contingencyService.toggleContingencyMode(!!this.contingencyModeControl.value);
    }

    closeClient(): void {
        this.dialog.open(ConfirmDialogComponent, {
            disableClose: true,
            data: {
                message: 'Você tem certeza que deseja <b>FECHAR</b> o <b>client</b>?<br><br>',
                disableCancel: false,
                confirmButtonValue: 'OK',
                icon: 'error_outline'
            }
        }).afterClosed().pipe(
            filter(Boolean),
            takeUntilDestroyed(this.destroyRef),
            switchMap(() => this.localClientService.exit$({reason: 'exit client'}))
        ).subscribe({
            next: (res) => {
                console.log('close client OK', res.status, res.body);
            },
            error: (err) => {
                console.error('close client error', err);
            }
        });

    }

    statusClient(): void {
        this.localClientService.status$({reason: 'status'}).subscribe({
            next: (res) => {
                this.alertMessage('<h2>Status client OK</h2><br>' + res.body + '<br><br>');
                console.log('status client OK', res.status, res.body);
            },
            error: (err) => {
                this.alertMessage('<h2>Status client Error</h2><br>' + err?.message + '<br><br>');
                console.error('close client error', err);
            }
        });
    }

    private alertMessage(message: string): void {
        this.dialog.open(ConfirmDialogComponent, {
            disableClose: true,
            data: {
                message: message,
                disableCancel: true,
                confirmButtonValue: 'OK',
                icon: 'error_outline'
            }
        }).afterClosed().pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => {
        });
    }

    toggleDarkMode(): void {
        this.isDarkMode = !this.isDarkMode;
        const theme = this.isDarkMode ? 'theme-blue-gray-dark' : 'theme-default';
        localStorage.setItem('r2cloud-color-theme', theme);
        this._fuseConfigService.config = {colorTheme: theme};
        this.cdr.markForCheck();
    }

    isWindowsOS(): boolean {
        return isWindowsOS();
    }
}
